import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
} from 'recharts';
import { TrendingUp, Edit3, X, Save, Loader2, Calendar } from 'lucide-react';
import { getStorePlusSMB, saveStorePlusSMB } from '../api';

const monoFont = "'JetBrains Mono', 'Fira Code', monospace";
const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtBRLcompact = (v) => {
  if (!v) return 'R$0';
  if (Math.abs(v) >= 1_000_000) return `R$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 10000) return `R$${(v / 1000).toFixed(1)}k`;
  if (Math.abs(v) >= 1000) return `R$${(v / 1000).toFixed(2)}k`;
  return `R$${v.toFixed(0)}`;
};
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const COLOR_SMB = '#10b981';
const COLOR_EA = '#ec4899';
const COLOR_DESEJAVEL = '#3b82f6';
const COLOR_META = '#9ca3af';

function StorePlusSMBChart({ onDataChanged }) {
  const hoje = new Date();
  const [year, setYear] = useState(hoje.getFullYear());
  const [month, setMonth] = useState(hoje.getMonth() + 1);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getStorePlusSMB(year, month);
      setEntries(r.data.entries || []);
    } catch { setEntries([]); }
    setLoading(false);
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  // Desejável AUTO = Meta * fator. O fator respeita o MÊS selecionado:
  //  - mês passado  → 1 (mês completo)
  //  - mês futuro   → 0 (nada esperado ainda)
  //  - mês atual    → dia_acumulado / dias_do_mês (pró-rata)
  const diasNoMes = new Date(year, month, 0).getDate();
  const today = new Date();
  const cmpSel = year * 12 + month;
  const cmpNow = today.getFullYear() * 12 + (today.getMonth() + 1);
  const fallbackDia = today.getDate();
  const fatorDoDia = (refDay) => {
    if (cmpSel < cmpNow) return 1;   // mês passado: completo
    if (cmpSel > cmpNow) return 0;   // mês futuro: nada
    return Math.min(refDay / diasNoMes, 1);  // mês atual: pró-rata
  };

  // Dataset: 1 linha por loja com 4 keys (smb, ea, desejavel, meta) → 3 bars agrupados side-by-side
  const chartData = entries.map(e => {
    const refDay = e.ref_date ? new Date(e.ref_date + 'T00:00:00').getDate() : fallbackDia;
    const fator = fatorDoDia(refDay);
    const metaTotal = (e.meta_smb || 0) + (e.meta_store_plus || 0);
    const realTotal = (e.smb_value || 0) + (e.store_plus_value || 0);
    return {
      label: e.label,
      unit: e.unit,
      smb: e.smb_value || 0,
      ea: e.store_plus_value || 0,
      desejavel: metaTotal * fator,
      meta: metaTotal,
      _meta_smb: e.meta_smb || 0,
      _meta_ea: e.meta_store_plus || 0,
      _real_total: realTotal,
    };
  });

  const ultUpdate = entries
    .map(e => e.updated_at)
    .filter(Boolean)
    .sort()
    .reverse()[0];

  return (
    <section style={{ marginTop: 30 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <TrendingUp size={18} color="#ec4899" />
          <h2 style={{ fontSize: 17, fontWeight: 800, color: '#0f172a' }}>Venda Store+ e SMB</h2>
          <span style={{ fontSize: 11.5, color: '#475569', fontFamily: monoFont }}>Acumulado do mês — preenchido manualmente</span>
          {loading && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite', color: '#fbbf24' }} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select value={month} onChange={e => setMonth(parseInt(e.target.value))} style={selectStyle}>
            {MESES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} style={selectStyle}>
            {[hoje.getFullYear() - 1, hoje.getFullYear(), hoje.getFullYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => setEditing({ all: true })} style={editBtnStyle}>
            <Edit3 size={13} /> Editar valores
          </button>
        </div>
      </div>

      {ultUpdate && (
        <p style={{ fontSize: 11, color: '#64748b', marginBottom: 10, fontFamily: monoFont }}>
          Última atualização: {new Date(ultUpdate).toLocaleString('pt-BR')}
        </p>
      )}

      <div style={{
        position: 'relative',
        background: 'radial-gradient(circle at top left, rgba(236,72,153,0.08) 0%, transparent 50%), linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        border: '1px solid #e2e8f0', borderRadius: 16,
        padding: '22px 22px 18px',
        boxShadow: '0 8px 32px rgba(15,23,42,0.04), inset 0 1px 0 rgba(15,23,42,0.04)',
        overflow: 'hidden',
      }}>
        {/* Ambient glow */}
        <div style={{
          position: 'absolute', top: -40, right: -40, width: 180, height: 180,
          background: 'radial-gradient(circle, rgba(236,72,153,0.18) 0%, transparent 70%)',
          filter: 'blur(40px)', pointerEvents: 'none',
        }} />
        {loading ? (
          <p style={{ color: '#475569', textAlign: 'center', padding: 40, position: 'relative' }}>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle' }} /> Carregando…
          </p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', marginBottom: 14, flexWrap: 'wrap', position: 'relative' }}>
              <Legenda cor={COLOR_SMB} label="SMB" />
              <Legenda cor={COLOR_EA} label="Store+ (EA)" />
              <Legenda cor={COLOR_DESEJAVEL} label="Desejável (auto)" />
              <Legenda cor={COLOR_META} label="Meta Samsung" />
            </div>
            <div style={{ width: '100%', height: 380, position: 'relative' }}>
              <ResponsiveContainer>
                <BarChart
                  data={chartData}
                  margin={{ top: 36, right: 18, left: 8, bottom: 16 }}
                  barCategoryGap="22%"
                  barGap={6}
                >
                  <defs>
                    <linearGradient id="gradSMB" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#34d399" stopOpacity={0.95}/>
                      <stop offset="100%" stopColor="#059669" stopOpacity={0.9}/>
                    </linearGradient>
                    <linearGradient id="gradEA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f472b6" stopOpacity={0.95}/>
                      <stop offset="100%" stopColor="#db2777" stopOpacity={0.9}/>
                    </linearGradient>
                    <linearGradient id="gradDesej" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.95}/>
                      <stop offset="100%" stopColor="#2563eb" stopOpacity={0.9}/>
                    </linearGradient>
                    <linearGradient id="gradMeta" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#d4d4d8" stopOpacity={0.9}/>
                      <stop offset="100%" stopColor="#71717a" stopOpacity={0.85}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" strokeOpacity={0.6} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 13, fill: '#1e293b', fontWeight: 700 }}
                    axisLine={{ stroke: '#e2e8f0' }}
                    tickLine={false}
                    interval={0}
                    dy={6}
                  />
                  <YAxis
                    tick={{ fontSize: 10.5, fill: '#64748b', fontFamily: monoFont }}
                    tickFormatter={fmtBRLcompact}
                    axisLine={false}
                    tickLine={false}
                    width={70}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(15,23,42,0.04)' }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      const p = payload[0]?.payload || {};
                      const smb = p.smb || 0, ea = p.ea || 0;
                      const realTotal = smb + ea;
                      const metaTotal = (p._meta_smb || 0) + (p._meta_ea || 0);
                      const pct = metaTotal > 0 ? (realTotal / metaTotal) * 100 : 0;
                      return (
                        <div style={{
                          background: 'rgba(255,255,255,0.96)', border: '1px solid rgba(236,72,153,0.40)',
                          borderRadius: 12, padding: '12px 16px',
                          boxShadow: '0 12px 32px rgba(15,23,42,0.5), 0 0 0 1px rgba(236,72,153,0.15)',
                          backdropFilter: 'blur(10px)',
                          fontFamily: '-apple-system, sans-serif',
                          minWidth: 240,
                        }}>
                          <p style={{ color: '#0f172a', fontWeight: 800, fontSize: 14, marginBottom: 10 }}>{label}</p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <Row cor={COLOR_SMB} label="SMB realizado" valor={smb} />
                            <Row cor={COLOR_EA} label="Store+ realizado" valor={ea} />
                            <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }} />
                            <Row cor="#b45309" label="Total realizado" valor={realTotal} bold />
                            <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }} />
                            <Row cor={COLOR_DESEJAVEL} label="Desejável (auto)" valor={p.desejavel} />
                            <Row cor={COLOR_META} label="Meta total" valor={p.meta} />
                            <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: 11, color: '#475569' }}>Atingimento</span>
                              <span style={{ fontSize: 13, color: pct >= 100 ? '#15803d' : pct >= 80 ? '#b45309' : '#dc2626', fontFamily: monoFont, fontWeight: 800 }}>{pct.toFixed(1)}%</span>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="smb" stackId="real" fill="url(#gradSMB)" name="SMB" maxBarSize={56} />
                  <Bar dataKey="ea" stackId="real" fill="url(#gradEA)" name="Store+ (EA)" radius={[8, 8, 0, 0]} maxBarSize={56}>
                    <LabelList
                      valueAccessor={(d) => (d.smb + d.ea) > 0 ? fmtBRLcompact(d.smb + d.ea) : ''}
                      position="top"
                      style={{ fontSize: 11, fill: '#b45309', fontFamily: monoFont, fontWeight: 800 }}
                    />
                  </Bar>
                  <Bar dataKey="desejavel" fill="url(#gradDesej)" name="Desejável" radius={[8, 8, 0, 0]} maxBarSize={56}>
                    <LabelList
                      valueAccessor={(d) => d.desejavel > 0 ? fmtBRLcompact(d.desejavel) : ''}
                      position="top"
                      style={{ fontSize: 11, fill: '#1d4ed8', fontFamily: monoFont, fontWeight: 800 }}
                    />
                  </Bar>
                  <Bar dataKey="meta" fill="url(#gradMeta)" name="Meta Samsung" radius={[8, 8, 0, 0]} maxBarSize={56}>
                    <LabelList
                      valueAccessor={(d) => d.meta > 0 ? fmtBRLcompact(d.meta) : ''}
                      position="top"
                      style={{ fontSize: 11, fill: '#1e293b', fontFamily: monoFont, fontWeight: 800 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Footer cards-resumo por loja */}
            <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: `repeat(${Math.max(chartData.length, 1)}, 1fr)`, gap: 10 }}>
              {chartData.map(d => {
                const realTotal = (d.smb || 0) + (d.ea || 0);
                const meta = d.meta || 0;
                const desej = d.desejavel || 0;
                const pct = meta > 0 ? (realTotal / meta) * 100 : 0;
                const pctDesej = desej > 0 ? (realTotal / desej) * 100 : 0;
                const statusCor = pct >= 100 ? '#15803d' : pctDesej >= 100 ? '#b45309' : '#dc2626';
                return (
                  <div key={d.unit} style={{
                    background: 'linear-gradient(180deg, rgba(15,23,42,0.025) 0%, rgba(15,23,42,0.02) 100%)',
                    border: `1px solid ${statusCor}30`,
                    borderRadius: 12, padding: '10px 12px',
                    boxShadow: `inset 0 1px 0 rgba(15,23,42,0.04), 0 0 20px ${statusCor}10`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: '#1e293b', letterSpacing: '0.04em' }}>{d.label}</span>
                      <span style={{ fontSize: 10, fontWeight: 800, color: statusCor, fontFamily: monoFont }}>{pct.toFixed(1)}%</span>
                    </div>
                    <p style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', fontFamily: monoFont, letterSpacing: '-0.02em' }}>
                      {fmtBRLcompact(realTotal)}
                    </p>
                    <p style={{ fontSize: 9.5, color: '#64748b', marginTop: 2, fontFamily: monoFont }}>
                      desej {fmtBRLcompact(desej)} · meta {fmtBRLcompact(meta)}
                    </p>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {editing && (
        <EditModal
          entries={entries}
          year={year}
          month={month}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onDataChanged?.(); }}
        />
      )}
    </section>
  );
}

function Row({ cor, label, valor, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
      <span style={{ fontSize: 11.5, color: cor, fontWeight: bold ? 800 : 600 }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: cor, marginRight: 6, verticalAlign: 'middle' }} />
        {label}
      </span>
      <span style={{ fontSize: bold ? 13 : 12, color: cor, fontFamily: monoFont, fontWeight: bold ? 800 : 700 }}>{fmtBRL(valor)}</span>
    </div>
  );
}

function Legenda({ cor, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#334155' }}>
      <span style={{ width: 11, height: 11, borderRadius: 3, background: cor, display: 'inline-block' }} />
      {label}
    </span>
  );
}

const selectStyle = {
  padding: '7px 10px', borderRadius: 8, border: '1px solid #cbd5e1',
  background: '#f8fafc', color: '#0f172a', fontSize: 12, outline: 'none',
};
const editBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '7px 12px', borderRadius: 8, border: '1px solid rgba(236,72,153,0.30)',
  background: 'rgba(236,72,153,0.10)', color: '#be185d',
  fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
};

function EditModal({ entries, year, month, onClose, onSaved }) {
  const [forms, setForms] = useState(() => entries.map(e => ({ ...e })));
  const [saving, setSaving] = useState(false);
  const [refDate, setRefDate] = useState(() => {
    // Respeita o mês selecionado: se for o mês atual, "ontem"; senão, o ÚLTIMO dia
    // do mês selecionado (evita gravar data de outro mês, ex: julho num edit de junho).
    const hoje = new Date();
    const ehMesAtual = hoje.getFullYear() === year && (hoje.getMonth() + 1) === month;
    let d;
    if (ehMesAtual) { d = new Date(); d.setDate(d.getDate() - 1); }
    else { d = new Date(year, month, 0); }  // último dia do mês selecionado
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });

  const update = (idx, field, val) => {
    setForms(f => f.map((x, i) => i === idx ? { ...x, [field]: val } : x));
  };

  const save = async () => {
    setSaving(true);
    try {
      for (const f of forms) {
        await saveStorePlusSMB({
          year, month, unit: f.unit,
          smb_value: parseFloat(f.smb_value) || 0,
          store_plus_value: parseFloat(f.store_plus_value) || 0,
          desejavel_smb: 0,  // auto-calculado no front
          desejavel_store_plus: 0,
          meta_smb: parseFloat(f.meta_smb) || 0,
          meta_store_plus: parseFloat(f.meta_store_plus) || 0,
          ref_date: refDate || null,
        }, 'admin');
      }
      onSaved();
    } catch (e) {
      alert('Erro ao salvar: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const inputStyle = {
    width: '100%', padding: '6px 8px', borderRadius: 6,
    border: '1px solid #cbd5e1', background: '#f8fafc', color: '#0f172a',
    fontSize: 11.5, outline: 'none', fontFamily: monoFont, textAlign: 'right',
    minWidth: 90,
  };
  const labelHeaderStyle = {
    fontSize: 9.5, fontWeight: 800, color: '#475569',
    textTransform: 'uppercase', letterSpacing: '0.12em', padding: '6px 8px',
    borderBottom: '1px solid #e2e8f0', textAlign: 'left',
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 90,
      background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        border: '1px solid #ec489940', borderRadius: 16,
        width: '100%', maxWidth: 980, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '14px 22px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <TrendingUp size={18} color="#ec4899" />
            <p style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Editar Venda Store+ e SMB — {MESES[month-1]}/{year}</p>
          </div>
          <button onClick={onClose} aria-label="Fechar" style={{
            background: 'rgba(15,23,42,0.08)', border: 'none', borderRadius: 8,
            padding: 6, cursor: 'pointer', color: '#334155', display: 'inline-flex',
          }}><X size={16} /></button>
        </div>

        <div style={{ padding: '12px 22px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Calendar size={14} color="#475569" />
          <label style={{ fontSize: 11, color: '#475569' }}>Acumulado até o dia:</label>
          <input type="date" value={refDate || ''} onChange={e => setRefDate(e.target.value)} style={{ ...inputStyle, width: 160, textAlign: 'left' }} />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ ...labelHeaderStyle, verticalAlign: 'bottom' }}>Unidade</th>
                <th colSpan={2} style={{ ...labelHeaderStyle, textAlign: 'center', borderBottom: 'none', paddingBottom: 0, color: '#0e7490' }}>Realizado (acumulado)</th>
              </tr>
              <tr>
                <th style={{ ...labelHeaderStyle, textAlign: 'right', color: '#047857' }}>SMB</th>
                <th style={{ ...labelHeaderStyle, textAlign: 'right', color: '#ec4899' }}>Store+</th>
              </tr>
            </thead>
            <tbody>
              {forms.map((f, idx) => (
                <tr key={f.unit}>
                  <td style={{ padding: '10px 8px', color: '#0f172a', fontWeight: 700, borderBottom: '1px solid rgba(226,232,240,0.8)' }}>
                    {f.label}
                  </td>
                  <td style={{ padding: '6px 4px', borderBottom: '1px solid rgba(226,232,240,0.8)' }}>
                    <input type="number" step="0.01" value={f.smb_value} onChange={e => update(idx, 'smb_value', e.target.value)} style={inputStyle} placeholder="0" />
                  </td>
                  <td style={{ padding: '6px 4px', borderBottom: '1px solid rgba(226,232,240,0.8)' }}>
                    <input type="number" step="0.01" value={f.store_plus_value} onChange={e => update(idx, 'store_plus_value', e.target.value)} style={inputStyle} placeholder="0" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ marginTop: 14, fontSize: 10.5, color: '#64748b', fontFamily: monoFont }}>
            Só o REALIZADO (acumulado em R$) é editado aqui. A <b>meta</b> vem da aba <b>Metas e Equipe</b> (preenchida pelas lojas) e não é mais editável neste painel.
            Desejável = Meta × (dias decorridos ÷ total do mês) — mês passado conta como completo.
          </p>
        </div>

        <div style={{ padding: '12px 22px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(15,23,42,0.08)',
            background: 'rgba(15,23,42,0.04)', color: '#334155',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>Cancelar</button>
          <button onClick={save} disabled={saving} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: 'linear-gradient(180deg, #ec4899, #db2777)', color: '#fff',
            fontSize: 12, fontWeight: 800, cursor: saving ? 'wait' : 'pointer',
            boxShadow: '0 4px 12px rgba(236,72,153,0.35)',
          }}>
            {saving ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={13} />}
            {saving ? 'Salvando…' : 'Salvar tudo'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default StorePlusSMBChart;
